import { useCallback, useRef } from 'react';
import _ from 'lodash';
import { cloneData } from '@helpers/helpers';
import useJobStore from '../../../stores/job/Job.store';

/**
 * Saved-state baseline for an exclusive JEV panel (the replica discipline of the JEV save
 * data-flow audit: single writer, ship only dirty, rehydrate).
 *
 * `track(payload, links)` is called from the panel's change/onUpdate path: the first call after
 * (re)load seeds the baseline from server state; later calls arm the pending change when the
 * payload differs from the baseline and clear it when the user edits back to the baseline.
 * `rebaseline(data)` is called after a successful save: the just-saved payload becomes the new
 * baseline and the pending change is cleared (the form registry's own later clear is then a
 * no-op, so this hook — not the registry commit edge — owns the saved-state advance).
 *
 * Baselines are deep-cloned: panel editors may mutate the arrays they hand out, and an aliased
 * baseline would make a real edit compare equal to "saved".
 *
 * The baseline is mirrored into the store as `components[componentId].form.data` (merged in, so
 * the backend-hydrated `_links` survive), giving the panel a saved-state replica that
 * cross-panel readers and `clearPendingFormChange`'s commit edge can rely on.
 *
 * Adopting this in a panel requires its pending payload shape to equal its saved-state shape
 * (approval_reviewers, for example, differs today — reshape before adopting).
 */
export default function useSavedBaseline(componentId) {
    const initialPayloadRef = useRef(null);
    const latestPayloadRef = useRef(null);
    const { setPendingFormChange, clearPendingFormChange, getPendingFormChange, updateComponent } = useJobStore();

    const rebaseline = useCallback((data) => {
        initialPayloadRef.current = _.cloneDeep(data);
        latestPayloadRef.current = data;
        updateComponent(componentId, { form: { data: cloneData(data) } });
        clearPendingFormChange(componentId);
    }, [componentId, updateComponent, clearPendingFormChange]);

    const track = useCallback((payload, links) => {
        latestPayloadRef.current = payload;
        if (initialPayloadRef.current === null) {
            initialPayloadRef.current = _.cloneDeep(payload);
            updateComponent(componentId, { form: { data: cloneData(payload) } });
            return;
        }
        if (_.isEqual(payload, initialPayloadRef.current)) {
            if (getPendingFormChange(componentId)) {
                clearPendingFormChange(componentId);
            }
        } else {
            setPendingFormChange(componentId, payload, links);
        }
    }, [componentId, setPendingFormChange, clearPendingFormChange, getPendingFormChange, updateComponent]);

    const resetBaseline = useCallback(() => {
        initialPayloadRef.current = null;
        latestPayloadRef.current = null;
    }, []);

    return { track, rebaseline, resetBaseline, latestPayloadRef };
}
